import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";

interface BannerDetail {
  title: string;
  content: string;
}

interface BannerDetailData {
  intro: string;
  bannerDetail: BannerDetail[];
}

const EditBannerDetail = () => {
  // State for storing advantages content
  const [bannerDetailData, setBannerDetailData] = useState<BannerDetailData>({
    intro: "",
    bannerDetail: [],
  });

  // State for tracking which section is being edited
  const [editingSection, setEditingSection] = useState<string | null>(null);
  // State for tracking which advantage is being edited
  const [editingBannerDetailIndex, setEditingBannerDetailIndex] = useState<
    number | null
  >(null);
  // State for adding new advantage
  const [isAddingNew, setIsAddingNew] = useState(false);
  // Temporary state for editing
  const [editText, setEditText] = useState("");
  const [editTitle, setEditTitle] = useState("");
  // Loading state
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [advantageToDelete, setAdvantageToDelete] = useState<number | null>(
    null
  );

  // Fetch data from Firebase on component mount
  useEffect(() => {
    const fetchAdvantagesData = async () => {
      try {
        const bannerDetailRef = ref(db, "LayoutSettingData/BannerDetail");
        const snapshot = await get(bannerDetailRef);

        if (snapshot.exists()) {
          setBannerDetailData(snapshot.val());
        } else {
          // If no data exists, initialize with default content
          const initialData: BannerDetailData = {
            intro:
              "Your one-stop solution for all your catering needs. From fresh groceries to catering supplies, we have you covered.",
            bannerDetail: [
              {
                title: "Sales",
                content:
                  "firstly we have a sales section where you can find all the products that are on sale.",
              },
            ],
          };
          setBannerDetailData(initialData);
          await saveToFirebase(initialData);
        }
      } catch (error) {
        console.error("Error fetching banner detail data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvantagesData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data = bannerDetailData) => {
    try {
      const bannerDetailRef = ref(db, "LayoutSettingData/BannerDetail");
      await set(bannerDetailRef, data);
      toast({
        title: "Success",
        description: "Changes saved successfully",
        variant: "success",
        showIcon: true,
      });
      return true;
    } catch (error) {
      console.error("Error saving advantages data:", error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
        showIcon: true,
      });
      return false;
    }
  };

  // Handle edit intro button click
  const handleEditIntro = () => {
    setEditingSection("intro");
    setEditText(bannerDetailData.intro);
  };

  // Handle edit advantage button click
  const handleEditAdvantage = (index: number) => {
    setEditingBannerDetailIndex(index);
    setEditTitle(bannerDetailData.bannerDetail[index].title);
    setEditText(bannerDetailData.bannerDetail[index].content);
  };

  // Handle add new advantage button click
  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditTitle("");
    setEditText("");
  };

  // Handle delete advantage button click
  const handleDelete = (index: number) => {
    setAdvantageToDelete(index);
    setShowDeleteDialog(true);
  };

  // Handle save intro button click
  const handleSaveIntro = async () => {
    const updatedData = { ...bannerDetailData, intro: editText };
    const success = await saveToFirebase(updatedData);
    if (success) {
      setBannerDetailData(updatedData);
      setEditingSection(null);
    }
  };

  // Handle save advantage button click
  const handleSaveAdvantage = async () => {
    if (editingBannerDetailIndex !== null) {
      const updatedBannerDetail = [...bannerDetailData.bannerDetail];
      updatedBannerDetail[editingBannerDetailIndex] = {
        title: editTitle,
        content: editText,
      };
      const updatedData = {
        ...bannerDetailData,
        bannerDetail: updatedBannerDetail,
      };
      const success = await saveToFirebase(updatedData);
      if (success) {
        setBannerDetailData(updatedData);
        setEditingBannerDetailIndex(null);
      }
    }
  };

  // Handle save new advantage button click
  const handleSaveNew = async () => {
    if (editTitle.trim() === "" || editText.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter both title and content",
        variant: "destructive",
        showIcon: true,
      });
      return;
    }

    const newAdvantage = {
      title: editTitle,
      content: editText,
    };

    // Ensure bannerDetail is an array before spreading
    const currentBannerDetail = Array.isArray(bannerDetailData.bannerDetail)
      ? bannerDetailData.bannerDetail
      : [];

    const updatedBannerDetail = [...currentBannerDetail, newAdvantage];
    const updatedData = {
      ...bannerDetailData,
      bannerDetail: updatedBannerDetail,
    };

    const success = await saveToFirebase(updatedData);
    if (success) {
      setBannerDetailData(updatedData);
      setIsAddingNew(false);
      setEditTitle("");
      setEditText("");
      toast({
        title: "Success",
        description: "New banner detail added successfully",
        variant: "success",
        showIcon: true,
      });
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    setEditingSection(null);
    setEditingBannerDetailIndex(null);
    setIsAddingNew(false);
    setEditTitle("");
    setEditText("");
  };

  const confirmDelete = async () => {
    if (advantageToDelete !== null) {
      const updatedBannerDetail = [...bannerDetailData.bannerDetail];
      updatedBannerDetail.splice(advantageToDelete, 1);
      const updatedData = {
        ...bannerDetailData,
        bannerDetail: updatedBannerDetail,
      };
      const success = await saveToFirebase(updatedData);
      if (success) {
        setBannerDetailData(updatedData);
        toast({
          title: "Success",
          description: "Banner detail deleted successfully",
          variant: "success",
          showIcon: true,
        });
      }
    }
    setShowDeleteDialog(false);
    setAdvantageToDelete(null);
  };

  // Render intro section
  const renderIntro = () => {
    return (
      <div className="bg-gray-50 rounded-lg p-6">
        {editingSection === "intro" ? (
          <div className="space-y-4">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
            />
            <div className="flex space-x-3">
              <button
                onClick={handleSaveIntro}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <p className="leading-relaxed">{bannerDetailData.intro}</p>
            <button
              className="text-customGreenColor hover:text-opacity-80 transition-all ml-3 flex-shrink-0"
              onClick={handleEditIntro}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render advantage item
  const renderAdvantage = (advantage: BannerDetail, index: number) => {
    return (
      <div key={index} className="bg-gray-50 rounded-lg p-6 shadow-sm">
        {editingBannerDetailIndex === index ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={50}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                maxLength={350}
                className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div className="flex space-x-3 pt-2">
              <button
                onClick={handleSaveAdvantage}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-helveticaBold text-2xl">{advantage.title}</h2>
              <div className="flex space-x-3">
                <button
                  className="text-customGreenColor hover:text-opacity-80 transition-all"
                  onClick={() => handleEditAdvantage(index)}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  className="text-red-500 hover:text-opacity-80 transition-all"
                  onClick={() => handleDelete(index)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="whitespace-pre-line leading-relaxed">
              {advantage.content}
            </p>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            Banner Detail
          </h1>
          <button
            className="text-white w-full md:w-auto bg-customGreenColor justify-center hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center"
            onClick={handleAddNew}
          >
            <PlusIcon className="h-4 w-4 mr-2" /> Add Banner Detail
          </button>
        </div>

        {/* Intro section */}
        {/* {renderIntro()} */}

        {/* Add New Advantage Form */}
        {isAddingNew && (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 className="font-helveticaBold text-black text-xl mb-4">
              Add New Banner Detail
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter advantage title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter advantage content"
                />
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleSaveNew}
                  className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
                >
                  <CheckIcon className="h-4 w-4 mr-2" /> Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
                >
                  <XIcon className="h-4 w-4 mr-2" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Advantages List */}
        <div className="grid gap-6">
          {Array.isArray(bannerDetailData?.bannerDetail) &&
            bannerDetailData.bannerDetail.map((bannerDetail, index) =>
              renderAdvantage(bannerDetail, index)
            )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setAdvantageToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setAdvantageToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this advantage? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditBannerDetail;
